import Box from '@mui/material/Box';
import { isDarkColor } from 'common/utilities/colors/colors';
import { arrayLast } from 'common/utilities/core/array';
import React from 'react';
import type { SequenceFeature } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import type { ColumnsLetterOccurrences } from 'sequenceAnalyser/utilities/colorCalculations/colorCalculations';
import type { SelectedColorScheme } from 'sequenceAnalyser/utilities/colors';
import { featuresForRegion } from 'sequenceAnalyser/utilities/features/features';
import residueProcessor from 'sequenceAnalyser/utilities/residueProcessor/residueProcessor';
import type { FeatureDefinitions, SequenceAnalyserTableView} from 'sequenceAnalyser/utilities/sequenceAnalyser.types';
import { SequenceAnalyserTableViews } from 'sequenceAnalyser/utilities/sequenceAnalyser.types';
import { buildFeatureId } from 'sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser';

import classes from './PrintViewResidue.style';

interface ComponentProps {
  featureDefinitions: FeatureDefinitions;
  view: SequenceAnalyserTableView;
  columnsLetterOccurrences: ColumnsLetterOccurrences;
  colors?: SelectedColorScheme;
  sequenceId: number;
  residue: string;
  chunkPosition: number;
  residuePositionInChunk: number;
  rowId: number;
  features: SequenceFeature[];
  noFeaturesColors: boolean;
  hasFeatures: boolean;
  residuesPerChunk: number;
  hideResidueForView?: boolean;
  maskingResidue: string;
}

function PrintViewResidue(props: ComponentProps) {
  const {
    sequenceId, chunkPosition, residuePositionInChunk, residuesPerChunk,
    colors, hasFeatures, features, noFeaturesColors, featureDefinitions,
    residue, view, rowId, columnsLetterOccurrences, hideResidueForView, maskingResidue,
  } = props;

  const hasColors = colors !== undefined;

  const {
    residueForView,
    visibleDefs,
    isFeatureColorDark,
    color,
    colorOpacity,
    position,
  } = residueProcessor({
    sequenceId,
    chunkPosition,
    residuePositionInChunk,
    residuesPerChunk,
    hasFeatures,
    features,
    noFeaturesColors,
    featureDefinitions,
    residue,
    view,
    rowId,
    columnsLetterOccurrences,
    colors,
    maskingResidue,
  });

  let residueFeatures = featuresForRegion(features, position);

  // Only show the last feature as it is shown in the sequence table
  if (residueFeatures.length > 0) residueFeatures = [arrayLast(residueFeatures)];

  const viewSettingHideFeatures = rowId !== 0
    && residueForView === '-'
    && view === SequenceAnalyserTableViews.abbreviatedWithoutColorsAndFeaturesColors;

  return (
    <Box sx={classes.residueWrapper}>
      <Box
        sx={classes.residue}
        className={[
          // If a color is not set, show features overlap stripes on the whole element
          !noFeaturesColors && !hasColors && visibleDefs.length > 1
            ? `${isFeatureColorDark ? 'darkFeatureStrippes' : 'lightFeatureStrippes'}`
            : '',
          !color || !isDarkColor(color) ? 'lightBg' : 'darkBg',
        ].join(' ')}
        style={{
          backgroundColor: color ? `rgba(${color.join(',')}, ${colorOpacity})` : undefined,
          color: hideResidueForView ? 'transparent' : undefined,
        }}
      >
        {residueForView}
      </Box>
      {/* If features are expanded don't show features within the residue */}
      {hasColors && !noFeaturesColors && !viewSettingHideFeatures && (
        <>
          {/* If a features AND color are set, show a thinner version of the stripes overlapping features */}
          {hasFeatures && visibleDefs.length > 1 && (
            <div className={`thinFeatureStrippes ${isFeatureColorDark ? 'darkBg' : 'lightBg'}`} />
          )}
          {residueFeatures.map((feature) => (
            <Box
              key={`feature-${feature.id}`}
              sx={classes.feature}
              style={{
                backgroundColor: `rgb(${featureDefinitions[buildFeatureId(feature)].color.join(',')})`,
              }}
            />
          ))}
        </>
      )}
    </Box>
  );
}

export default PrintViewResidue;
